import colorbrewer from "colorbrewer";
import { interpolate, interpolateRgbBasis, scaleLinear } from "d3";
import { parseToRgb, readableColor, rgb, saturate, toColorString } from "polished";
import { getRandomFloat, getRandomInt } from "widgets/utils";

import { COLOR_SCALE_MAX_BIT_SCORE, COLOR_SCALE_MIN_BIT_SCORE, VARIANT_EFFECT } from "@/settings/settings.ts";

import { SEQUENCE_TYPE_COLORS } from "./genome-browser-color.js";

export const NOT_FOUND_COLOR = "#666666";
const GENE_TYPE_COLORS: Map<string, string> = createGeneTypeColorMap();
//const VARIANT_TYPE_COLORS: Map<string, string> = createVariantTypeColorMap();
const VARIANT_EFFECT_COLORS: Map<string, string> = createVariantEffectColorMap();

// https://sashamaps.net/docs/resources/20-colors/
export const DISTINCT_COLORS = [
  "#e6194b",
  "#3cb44b",
  "#ffe119",
  "#4363d8",
  "#f58231",
  "#911eb4",
  "#46f0f0",
  "#f032e6",
  "#bcf60c",
  "#fabebe",
  "#008080",
  "#e6beff",
  "#9a6324",
  "#fffac8",
  "#800000",
  "#aaffc3",
  "#808000",
  "#ffd8b1",
  "#000075",
  "#808080",
  "#e2dede",
  "#000000",
];
// Gradient color scale for bit score
export const RAINBOW_COLORS = [
  "#2c7bb6",
  "#00a6ca",
  "#00ccbc",
  "#90eb9d",
  "#ffff8c",
  "#f9d057",
  "#f29e2e",
  "#e76818",
  "#d7191c",
];
export const rainbowColorInterpolator = interpolateRgbBasis(RAINBOW_COLORS);
const bitScoreScale = scaleLinear()
  .domain([COLOR_SCALE_MIN_BIT_SCORE, COLOR_SCALE_MAX_BIT_SCORE])
  .range([0, 1])
  .clamp(true);

// Gradient color scale (hot colors) for content browser (based on percentage)
export const HOT_PERCENTAGE_COLORS = ["#fee08b", "#fdae61", "#f46d43", "#d53e4f", "#9e0142"];
export const hotPercentageColorInterpolator = interpolateRgbBasis(HOT_PERCENTAGE_COLORS);
//const hotPercentageScale = scaleLinear().domain([0, 100]).range([0, 1]).clamp(true);

// Gradient color scale (cold colors) for content browser (based on percentage)
export const COLD_PERCENTAGE_COLORS = ["#e6f598", "#abdda4", "#66c2a5", "#3288bd", "#5e4fa2"];
export const coldPercentageColorInterpolator = interpolateRgbBasis(COLD_PERCENTAGE_COLORS);
//const coldPercentageScale = scaleLinear().domain([0, 100]).range([0, 1]).clamp(true);

function createGeneTypeColorMap() {
  const colorMap: Map<string, string> = new Map<string, string>();
  colorMap.set("mRNA", SEQUENCE_TYPE_COLORS[4]);
  colorMap.set("pseudogene", colorbrewer.Pastel2[8][1]);
  colorMap.set("unknown", colorbrewer.Pastel2[8][2]);
  colorMap.set("rRNA", SEQUENCE_TYPE_COLORS[5]);
  colorMap.set("tRNA", SEQUENCE_TYPE_COLORS[6]);
  colorMap.set("EST", SEQUENCE_TYPE_COLORS[12]);
  colorMap.set("snoRNA", SEQUENCE_TYPE_COLORS[15]);
  colorMap.set("snRNA", SEQUENCE_TYPE_COLORS[16]);
  colorMap.set("lncRNA", SEQUENCE_TYPE_COLORS[17]);
  colorMap.set("ncRNA", SEQUENCE_TYPE_COLORS[18]);
  // other DNA types (not existing as gene types)
  colorMap.set("3'-UTR", SEQUENCE_TYPE_COLORS[7]);
  colorMap.set("5'-UTR", SEQUENCE_TYPE_COLORS[8]);
  colorMap.set("Probe", SEQUENCE_TYPE_COLORS[9]);
  colorMap.set("Probeset", SEQUENCE_TYPE_COLORS[11]);
  colorMap.set("DNA", SEQUENCE_TYPE_COLORS[10]);
  colorMap.set("Read", SEQUENCE_TYPE_COLORS[13]);
  colorMap.set("Linear Contig", SEQUENCE_TYPE_COLORS[1]);
  colorMap.set("Circular Contig", SEQUENCE_TYPE_COLORS[3]);
  colorMap.set("Vector", SEQUENCE_TYPE_COLORS[14]);
  colorMap.set("Protein", SEQUENCE_TYPE_COLORS[2]);
  return colorMap;
}

/*
function createVariantTypeColorMap() {
  const colorMap: Map<string, string> = new Map<string, string>();
  colorMap.set(VARIANT_TYPE.POINT, "#a6cee3");
  colorMap.set(VARIANT_TYPE.DELETION, "#fb9a99");
  colorMap.set(VARIANT_TYPE.INSERTION, "#b2df8a");
  colorMap.set(VARIANT_TYPE.INDEL, "#fdbf6f");
  colorMap.set(VARIANT_TYPE.TRANSLOCATION_DELETION, "#cab2d6");
  colorMap.set(VARIANT_TYPE.TRANSLOCATION_INSERTION, "#cab2d6");

  return colorMap;
}
*/

function createVariantEffectColorMap() {
  const colorMap = new Map<string, string>();
  const colorRed = "#e54658";
  const colorYellow = "#fac938";
  const colorGreen = "#66c2a5";

  // See Ensembl variant color scheme at http://www.ensembl.org/info/genome/variation/predicted_data.html
  colorMap.set(VARIANT_EFFECT.UNKNOWN, colorGreen);
  colorMap.set(VARIANT_EFFECT.GENETIC_ELEMENTS_MOVED, colorRed);
  colorMap.set(VARIANT_EFFECT.SYNONYMOUS_CODING, colorGreen);
  colorMap.set(VARIANT_EFFECT.SYNONYMOUS_STOP, colorGreen);
  colorMap.set(VARIANT_EFFECT.SYNONYMOUS_START, colorGreen);
  colorMap.set(VARIANT_EFFECT.NON_SYNONYMOUS_CODING, colorRed);
  colorMap.set(VARIANT_EFFECT.NON_SYNONYMOUS_START, colorRed);
  colorMap.set(VARIANT_EFFECT.NON_SYNONYMOUS_STOP, colorRed);
  colorMap.set(VARIANT_EFFECT.CODON_INSERTION, colorRed);
  colorMap.set(VARIANT_EFFECT.CODON_DELETION, colorRed);
  colorMap.set(VARIANT_EFFECT.STOP_LOST, colorRed);
  colorMap.set(VARIANT_EFFECT.EXON_DELETED, colorRed);
  colorMap.set(VARIANT_EFFECT.UTR_5_DELETED, colorYellow);
  colorMap.set(VARIANT_EFFECT.UTR_3_DELETED, colorYellow);
  colorMap.set(VARIANT_EFFECT.STOP_GAINED, colorRed);
  colorMap.set(VARIANT_EFFECT.CODON_CHANGE_PLUS_CODON_INSERTION, colorRed);
  colorMap.set(VARIANT_EFFECT.CODON_CHANGE_PLUS_CODON_DELETION, colorRed);
  colorMap.set(VARIANT_EFFECT.CODON_CHANGE, colorRed);
  colorMap.set(VARIANT_EFFECT.START_GAINED, colorRed);
  colorMap.set(VARIANT_EFFECT.START_LOST, colorRed);
  colorMap.set(VARIANT_EFFECT.SPLICING_POTENTIALLY_DISTURBED, colorYellow);
  colorMap.set(VARIANT_EFFECT.SPLICE_SITE_DONOR, colorYellow);
  colorMap.set(VARIANT_EFFECT.SPLICE_SITE_ACCEPTOR, colorYellow);
  colorMap.set(VARIANT_EFFECT.GENETIC_ELEMENTS_LOST, colorYellow);
  colorMap.set(VARIANT_EFFECT.GENETIC_ELEMENTS_GAINED, colorYellow);
  colorMap.set(VARIANT_EFFECT.FRAME_SHIFT, colorRed);

  return colorMap;
}

export function getGeneTypeColor(geneType: string): string {
  const color = GENE_TYPE_COLORS.get(geneType);

  return color ? color : NOT_FOUND_COLOR;
}

export function getVariantEffectColor(variantEffect: string): string {
  const color = VARIANT_EFFECT_COLORS.get(variantEffect);

  return color ? color : NOT_FOUND_COLOR;
}

export function createDiscreteColorGradient(colorHexString: string, gradientSteps: number): string[] {
  const colorInterpolation = interpolate(colorHexString, readableColor(colorHexString, "black", "white"));
  const gradients: string[] = [];

  for (let i = 0; i < gradientSteps; i++) {
    gradients.push(colorInterpolation((1 / gradientSteps) * i));
  }

  return gradients;
}

export function chooseUniqueColor(scheme: string[], usedColors: string[] = []): string {
  // Used colors do not have to be from scheme!
  const availableColors = scheme.filter((col) => !usedColors.includes(col));

  if (availableColors.length > 0) {
    return availableColors[getRandomInt(0, availableColors.length - 1)];
  }

  // We have a collision, all colors are in use. Create a random one.
  return saturate(
    getRandomFloat(0.5, 1.0000001),
    rgb(getRandomInt(0, 255), getRandomInt(0, 255), getRandomInt(0, 255)),
  );
}

export function getBitScoreFillColor(bitScore: number): string {
  return rainbowColorInterpolator(bitScoreScale(bitScore));
}

export function getCssColor(hexColor?: string | null): string {
  return hexColor ? (hexColor.startsWith("0x") ? `#${hexColor.substring(2)}` : hexColor) : "#e1e1e5";
}

function getHexaFromColorCode(color?: string): string {
  try {
    const rgbColor = parseToRgb(color);
    return toColorString(rgbColor);
  } catch (_e) {
    return null;
  }
}

export function getColorFromTerm(term: string): string {
  const word = term?.trim().toLowerCase();
  switch (word) {
    case "yes":
      return DISTINCT_COLORS[1];
    case "no":
      return DISTINCT_COLORS[0];
    default:
      return getHexaFromColorCode(term);
  }
}
